// Basic scenario inputs: attach an Actual staff plan, set OGS hub
// proportions and pick the Automation Solution Set to apply.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Users } from 'lucide-react';
import {
  flattenStaffPlanToByLevel,
  isActualStaffPlan,
  isOgsEligible,
  totalsForStaffPlan,
} from '@rc/engine';
import type {
  ClientProfile,
  Model,
  OgsConfig,
  OgsDisciplineConfig,
  StaffPlan,
} from '@rc/engine/types';
import { api } from '../lib/api';
import type { ModelSummary } from '../lib/api';
import { withGeoclientQuery } from '../lib/geoclientContext';
import OgsDisciplineRow from './intake/OgsCard';
import PresetBar from './intake/PresetBar';
import AutomationSolutionSetCard from './intake/AutomationSolutionSetCard';

interface Props {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
  models: ModelSummary[];
  onModelChange: (modelId: string) => void;
  modelSwitching?: boolean;
  clientId?: string;
  scenarioId?: string;
  geoclientId?: string;
}

export default function BasicScenarioIntake({
  model,
  profile,
  onChange,
  models,
  onModelChange,
  modelSwitching,
  clientId,
  scenarioId,
  geoclientId,
}: Props) {
  return (
    <div className="space-y-4">
      <div className="card">
        <div className="card-body space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Scenarios - Basic
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Impact is measured from the selected staff plan after OGS hub
                proportion and automation — not from budget-sized modeled FTE.
              </p>
            </div>
            <select
              className="input text-xs max-w-[200px]"
              value={model.id}
              disabled={modelSwitching}
              onChange={(e) => onModelChange(e.target.value)}
              title="Model (rates, hubs, time concentration)"
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <BasicStaffPlanCard
        profile={profile}
        onChange={onChange}
        clientId={clientId}
        scenarioId={scenarioId}
        geoclientId={geoclientId}
      />

      <BasicOgsCard model={model} profile={profile} onChange={onChange} />

      <AutomationSolutionSetCard
        profile={profile}
        onChange={onChange}
        clientId={clientId}
        geoclientId={geoclientId}
        modelId={model.id}
      />
    </div>
  );
}

function BasicStaffPlanCard({
  profile,
  onChange,
  clientId,
  scenarioId,
  geoclientId,
}: {
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
  clientId?: string;
  scenarioId?: string;
  geoclientId?: string;
}) {
  const linkedPlanId = profile.currentStaffPlanId;
  const [plan, setPlan] = useState<StaffPlan | null>(null);
  const [saved, setSaved] = useState<StaffPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!clientId || !linkedPlanId) {
      setPlan(null);
      return;
    }
    setLoading(true);
    api
      .getStaffPlan(clientId, linkedPlanId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {
        if (!cancelled) setPlan(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, linkedPlanId]);

  useEffect(() => {
    let cancelled = false;
    if (!clientId) {
      setSaved([]);
      return;
    }
    api
      .listStaffPlans(clientId)
      .then((list) => {
        if (!cancelled) setSaved((list || []).filter((p) => isActualStaffPlan(p)));
      })
      .catch(() => {
        if (!cancelled) setSaved([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const attach = async (id: string) => {
    if (!clientId || attaching) return;
    setAttaching(true);
    setAttachError(null);
    try {
      const full = await api.getStaffPlan(clientId, id);
      if (!isActualStaffPlan(full)) {
        setAttachError('Basic scenarios need an Actual staff plan.');
        return;
      }
      onChange({
        currentStaffPlanId: full.id,
        currentStaffByLevel: flattenStaffPlanToByLevel(full.cells || []),
        currentStaffByDiscipline: {},
        automationBasis: 'actual',
      });
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Could not load that plan');
    } finally {
      setAttaching(false);
    }
  };

  const totals = plan ? totalsForStaffPlan(plan) : null;
  const editHref = clientId
    ? withGeoclientQuery(
        `/clients/${clientId}/staff-plans/${linkedPlanId || 'new'}` +
          (scenarioId ? `?from=${scenarioId}` : ''),
        geoclientId,
      )
    : null;

  return (
    <div className="card">
      <div className="card-body space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Staff plan</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            The Actual roster used as the analysis base. OGS % re-splits On↔Hub
            for this scenario only — it does not change the saved plan.
          </p>
        </div>

        {linkedPlanId ? (
          <div className="rounded-lg border border-slate-200 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center shrink-0">
              <Users size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-800 truncate">
                {loading ? 'Loading plan…' : plan?.name || 'Staff plan'}
              </div>
              <div className="text-xs text-slate-500">
                {totals ? (
                  <>
                    <span className="tabular-nums font-medium text-slate-700">
                      {totals.total.toFixed(1)}
                    </span>{' '}
                    total FTE ·{' '}
                    <span className="tabular-nums">{totals.onTotal.toFixed(1)}</span> on ·{' '}
                    <span className="tabular-nums">{totals.hubTotal.toFixed(1)}</span> hub
                    (as saved)
                  </>
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select
                className="input text-xs max-w-[180px]"
                value={linkedPlanId}
                disabled={attaching || saved.length === 0}
                onChange={(e) => attach(e.target.value)}
              >
                {saved.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {editHref && (
                <Link to={editHref} className="btn-secondary shrink-0">
                  <Pencil size={14} /> Edit
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center space-y-3">
            <Users size={20} className="text-slate-400 mx-auto" />
            <div className="text-sm font-medium text-slate-700">Select a staff plan</div>
            <select
              className="input text-sm max-w-sm mx-auto"
              value=""
              disabled={attaching || saved.length === 0}
              onChange={(e) => {
                if (e.target.value) void attach(e.target.value);
              }}
            >
              <option value="">
                {saved.length === 0 ? 'No Actual plans yet' : 'Choose a saved plan…'}
              </option>
              {saved.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {attachError && <div className="text-xs text-rose-600">{attachError}</div>}
      </div>
    </div>
  );
}

function BasicOgsCard({
  model,
  profile,
  onChange,
}: {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
}) {
  const ogs: OgsConfig = profile.ogs || {};
  const channelIdsFor = (discId: string) => {
    const ids = Array.from(
      new Set(
        (model.spendPerFte || [])
          .filter((r) => r.disciplineId === discId)
          .map((r) => r.channelId),
      ),
    );
    return ids.length > 0 ? ids : ['all'];
  };
  const disciplines = (model.disciplines || [])
    .filter((d) => d.active !== false)
    .filter((d) => channelIdsFor(d.id).some((ch) => isOgsEligible(model, d.id, ch)));
  const hasHubs = (model.ogsHubs || []).length > 0;
  const allDisabled =
    hasHubs &&
    (model.disciplines || []).filter((d) => d.active !== false).length > 0 &&
    disciplines.length === 0;
  const disciplineConfigs = ogs.disciplines || [];
  const presets = model.ogsPresets || [];

  const setDisciplineConfig = (next: OgsDisciplineConfig) => {
    const nextCh = next.channelId ?? 'all';
    const idx = disciplineConfigs.findIndex(
      (d) => d.disciplineId === next.disciplineId && (d.channelId ?? 'all') === nextCh,
    );
    const nextList =
      idx >= 0
        ? disciplineConfigs.map((d, i) => (i === idx ? next : d))
        : [...disciplineConfigs, next];
    onChange({ ogs: { ...ogs, disciplines: nextList }, ogsPresetId: 'custom' });
  };

  const applyPreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    onChange({
      ogs: { ...ogs, disciplines: preset.disciplines.map((d) => ({ ...d })) },
      ogsPresetId: preset.id,
    });
  };

  return (
    <div className="card">
      <div className="card-body space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">OGS / Hub proportion</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Re-split each role&apos;s total FTE between Onshore and Hub using the same
            proportion controls as Advanced — applied to the selected staff plan.
          </p>
        </div>
        <PresetBar
          presets={presets}
          activeId={profile.ogsPresetId}
          onApply={applyPreset}
          onCustom={() => onChange({ ogsPresetId: 'custom' })}
        />
        {!hasHubs ? (
          <p className="text-xs text-amber-700">
            This model has no OGS hubs configured, so OGS cannot be enabled.
          </p>
        ) : allDisabled ? (
          <p className="text-xs text-slate-500">
            No disciplines are enabled for OGS in this model.
          </p>
        ) : (
          <div className="space-y-2">
            {disciplines.map((d) => (
              <OgsDisciplineRow
                key={d.id}
                discipline={d}
                model={model}
                configs={disciplineConfigs}
                onChange={setDisciplineConfig}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
